import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  ModalHeading,
  ModalSubHeading,
  CombineInputGroup,
} from "../Styles/InputStyles";
import * as Yup from "yup";
import { useMemo, useState } from "react";
import { MdAddLocationAlt, MdClose, MdEdit } from "react-icons/md";
import SelectLocationModal from "../SelectLocationModal";
import tw from "tailwind-styled-components";
import { useSelector } from "react-redux";
import Config, { bhk_options, landlord_property_type } from "../../Config";
import SelectOptionModal from "../SelectOptionModal";
import { Autocomplete } from "@react-google-maps/api";
import { useRef } from "react";

const PropertyDetails = ({ setShowAddLocation, step, setStep, setStepTwoData, stepTwoData }) => {
  const autocompleteRef = useRef(null);

  const handlePlaceSelect = (setFieldValue) => {
    const place = autocompleteRef.current.getPlace();

    if (place && place.formatted_address) {
      setFieldValue("address", place.formatted_address);
    }
  };

  const propertyTypeOptions = landlord_property_type.map(role => ({
    label: role.label,
    value: role.value,
  }));

  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isBhkModalOpen, setIsBhkModalOpen] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const locations = useSelector((state) => state.LocationsReducer.locations);
  const filteredLocations = useMemo(() => locations?.filter(l => l.id !== 0) || [], [locations]);

  const initialValues = stepTwoData || {
    buildingName: "",
    flatNo: "",
    address: "",
    pinCode: "",
    locationId: "",
    propertyType: "",
    inventorySubType: "",
  };

  const validationSchema = Yup.object({
    propertyType: Yup.string().required("Property Type is required"),
    inventorySubType: Yup.string().when("propertyType", {
      is: val =>
        Number(val) === Config.LANDLORD_PROPERTY_TYPE.APARTMENT_FLAT ||
        Number(val) === Config.LANDLORD_PROPERTY_TYPE.VILLA_INDEPENDENT_FLOOR,
      then: schema => schema.required("Please select BHK"),
      otherwise: schema => schema.notRequired(),
    }),
    address: Yup.string().min(5, "Address must be at least 5 characters").required("Address is required"),
    locationId: Yup.string().required("Location is required"),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const formData = {
      buildingName: values.buildingName,
      flatNo: values.flatNo,
      address: values.address,
      pinCode: values.pinCode || null,
      locationId: values.locationId,
      propertyType: values.propertyType,
      inventorySubType: values.inventorySubType,
    };

    setStepTwoData(formData);
    setStep(step + 1);
    setSubmitting(false);
  };

  const handleGoBack = () => setStep(step - 1);

  return (
    <Formik initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ values, setFieldValue, errors, touched, isSubmitting }) => {
        const getCurrentTypeLabel = () => {
          const selected = landlord_property_type.find(item => item.value === Number(values.propertyType));
          return selected ? selected.label : "Select property type";
        };

        const getUnitLabel = () => {
          const propertyType = Number(values.propertyType);

          if (
            propertyType === Config.LANDLORD_PROPERTY_TYPE.SHOP_SHOWROOM ||
            propertyType === Config.LANDLORD_PROPERTY_TYPE.OFFICE
          ) {
            return "Shop/Office No";
          }

          return "Flat/House No";
        };

        const getBhkLabel = () => {
          const selected = bhk_options.find(item => item.value === Number(values.inventorySubType));
          return selected ? selected.label : "Select BHK";
        };

        const handleTypeSelect = selectedValue => {
          const selectedType = Number(selectedValue);
          setFieldValue("propertyType", selectedType);

          const isResidential =
            selectedType === Config.LANDLORD_PROPERTY_TYPE.APARTMENT_FLAT ||
            selectedType === Config.LANDLORD_PROPERTY_TYPE.VILLA_INDEPENDENT_FLOOR;

          setIsTypeModalOpen(false);

          if (!isResidential) setFieldValue("inventorySubType", "");
        };

        const getLocationLabel = () => {
          const selectedLocation = filteredLocations.find(loc => loc.id === values.locationId);
          return selectedLocation ? selectedLocation.name : "Select Location";
        };

        const isResidential =
          Number(values.propertyType) === Config.LANDLORD_PROPERTY_TYPE.APARTMENT_FLAT ||
          Number(values.propertyType) === Config.LANDLORD_PROPERTY_TYPE.VILLA_INDEPENDENT_FLOOR;

        return (
          <Form>
            <ModalSubHeading>Step 2</ModalSubHeading>
            <ModalHeading>Property Type & Address</ModalHeading>

            <ScrollableContent>
              <Wrapper>
                <InputGroup>
                  <Label>Property Type *</Label>
                  <FieldWrappers onClick={() => setIsTypeModalOpen(true)}>
                    <Field
                      readOnly
                      value={getCurrentTypeLabel()}
                      className="w-full bg-transparent border-none outline-none cursor-pointer"
                    />
                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                  </FieldWrappers>
                  {touched.propertyType && errors.propertyType && (
                    <p className="text-rose-600 text-sm mt-1">{errors.propertyType}</p>
                  )}
                </InputGroup>

                {isResidential && (
                  <InputGroup>
                    <Label>BHK *</Label>
                    <FieldWrappers onClick={() => setIsBhkModalOpen(true)}>
                      <Field
                        readOnly
                        value={getBhkLabel()}
                        className="w-full bg-transparent border-none outline-none cursor-pointer"
                      />
                      <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                    </FieldWrappers>
                    {touched.inventorySubType && errors.inventorySubType && (
                      <p className="text-rose-600 text-sm mt-1">{errors.inventorySubType}</p>
                    )}
                  </InputGroup>
                )}

                <InputGroup>
                  <Label>Address *</Label>

                  <FieldWrappers className="w-full">
                    <Autocomplete
                      className="w-full"
                      onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                      onPlaceChanged={() => handlePlaceSelect(setFieldValue)}
                      options={{
                        componentRestrictions: { country: "in" },
                        types: ["geocode"],
                      }}
                    >
                      <input
                        type="text"
                        value={values.address}
                        onChange={(e) => {
                          setFieldValue("address", e.target.value);
                        }}
                        placeholder="Enter address"
                        className="w-full pr-6 bg-transparent border-none outline-none"
                      />
                    </Autocomplete>
                    {values.address && (
                      <button
                        type="button"
                        onClick={() => setFieldValue("address", "")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      >
                        <MdClose />
                      </button>
                    )}
                  </FieldWrappers>

                  {touched.address && errors.address && (
                    <p className="text-rose-600 text-sm mt-1">{errors.address}</p>
                  )}
                </InputGroup>

                <CombineInputGroup>
                  <InputGroup>
                    <Label>Building/Tower/Block</Label>
                    <FieldWrappers>
                      <Field name="buildingName" placeholder="Enter building name" />
                    </FieldWrappers>
                  </InputGroup>

                  <InputGroup>
                    <Label>{getUnitLabel()}</Label>
                    <FieldWrappers>
                      <Field
                        name="flatNo"
                        placeholder={`Enter ${getUnitLabel().toLowerCase()}`}
                      />
                    </FieldWrappers>
                  </InputGroup>
                </CombineInputGroup>
                <InputGroup>
                  <Label>Select Location *</Label>
                  <FieldWrappers onClick={() => setShowLocationModal(true)}>
                    <Field
                      readOnly
                      value={getLocationLabel()}
                      className="w-full bg-transparent border-none outline-none cursor-pointer"
                    />
                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                  </FieldWrappers>
                  {touched.locationId && errors.locationId && (
                    <p className="text-rose-600 text-sm mt-1">{errors.locationId}</p>
                  )}

                  <div className="flex justify-end mt-2">
                    <Location type="button" onClick={() => setShowAddLocation(true)}>
                      <MdAddLocationAlt className="mr-1 mt-0.5" /> Add Location
                    </Location>
                  </div>
                </InputGroup>
              </Wrapper>
            </ScrollableContent>

            <ActionSection>
              <SideCancelBtn type="button" onClick={handleGoBack}>
                Back
              </SideCancelBtn>
              <SubmitBtn type="submit" disabled={isSubmitting}>
                Next
              </SubmitBtn>
            </ActionSection>

            <SelectOptionModal
              isOpen={isTypeModalOpen}
              onClose={() => setIsTypeModalOpen(false)}
              title="Select Property Type"
              options={propertyTypeOptions}
              selectedValue={values.propertyType}
              onSelect={handleTypeSelect}
              searchPlaceholder="Search property type..."
            />

            <SelectOptionModal
              isOpen={isBhkModalOpen}
              onClose={() => setIsBhkModalOpen(false)}
              title="Select BHK"
              options={bhk_options}
              selectedValue={values.inventorySubType}
              onSelect={val => {
                setFieldValue("inventorySubType", Number(val));
                setIsBhkModalOpen(false);
              }}
              searchPlaceholder="Search BHK..."
            />

            <SelectLocationModal
              isOpen={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              options={filteredLocations}
              selectedValue={values.locationId}
              onSelect={val => {
                setFieldValue("locationId", val);
                setShowLocationModal(false);
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

const Location = tw.button`text-teal-500 text-sm flex hover:underline`;

export default PropertyDetails;

